import dayjs from "dayjs"
import utc from "dayjs/plugin/utc"
import { useCallback, useEffect, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import type { AppState } from "src/store"
import { shouldAllowAccess } from "src/utilities/helper"
import GetLeaseApprovalActions from "./crud/GetLeaseApprovalActions"
import GetLeaseApprovalRequests from "./crud/GetLeaseApprovalRequests"
import type { I_ApprovalLeaseAction, I_ApprovalLeaseRequest } from "./types"
import "./RequestsManager.css"
import { Button, Modal } from "react-bootstrap"
import approvalService from "src/components/approvals/ApprovalService"
import RequestTable, {
	type Column,
} from "src/components/approvals/components/RequestTable/RequestTable"
import ActionApproval from "src/components/approvals/crud/ActionApproval"
import CancelLeaseRequest from "src/components/approvals/crud/CancelLeaseRequest"
import Loader from "src/components/common/Loader"
import { refreshActionableRequestCount } from "src/store/LeaseApprovals/actions"
import snackBar from "src/utilities/snackBar/SnackBar"
import RequestDetailModal from "./RequestDetailModal/RequestDetailModal"

dayjs.extend(utc)

const formatDate = (iso: string | null | undefined) =>
	iso ? dayjs.utc(iso).local().format("MM/DD/YYYY") : ""

const pendingActionsColumns: Column<I_ApprovalLeaseAction>[] = [
	{ label: "Unit Number", accessor: "UnitNumber" },
	{ label: "Request Type", accessor: "RequestType" },
	{
		label: "Date Created",
		accessor: "CreatedDateUTC",
		render: (r) => formatDate(r.CreatedDateUTC),
	},
	{ label: "Requested By", accessor: "RequestedByName" },
]

const completedActionsColumns: Column<I_ApprovalLeaseAction>[] = [
	{ label: "Unit Number", accessor: "UnitNumber" },
	{ label: "Request Type", accessor: "RequestType" },
	{
		label: "Date Actioned",
		accessor: "ActionedDateUTC",
		render: (r) => formatDate(r.ActionedDateUTC),
	},
	{ label: "Requested By", accessor: "RequestedByName" },
	{ label: "Status", accessor: "Status" },
]

const activeRequestsColumns: Column<I_ApprovalLeaseRequest>[] = [
	{ label: "Unit Number", accessor: "UnitNumber" },
	{ label: "Request Type", accessor: "RequestType" },
	{
		label: "Date Created",
		accessor: "CreatedDateUTC",
		render: (r) => formatDate(r.CreatedDateUTC),
	},
	{ label: "Status", accessor: "Status" },
]

const completedRequestsColumns: Column<I_ApprovalLeaseRequest>[] = [
	{ label: "Unit Number", accessor: "UnitNumber" },
	{ label: "Request Type", accessor: "RequestType" },
	{
		label: "Date Actioned",
		accessor: "ActionedDateUTC",
		render: (r) => formatDate(r.ActionedDateUTC),
	},
	{ label: "Actioned By", accessor: "ActionedByName" },
	{ label: "Status", accessor: "Status" },
]

const RequestsManager = () => {
	const userId = useSelector((state: AppState) =>
		parseInt(state.user.userId, 10),
	)
	const [requests, setRequests] = useState<I_ApprovalLeaseRequest[]>([])
	const [actionables, setActionables] = useState<I_ApprovalLeaseAction[]>([])
	const [selectedRequest, setSelectedRequest] =
		useState<I_ApprovalLeaseRequest | null>(null)
	const [selectedAction, setSelectedAction] =
		useState<I_ApprovalLeaseAction | null>(null)
	const [loading, setLoading] = useState(true)
	const [fetchLeaseDetailsOnly, setFetchLeaseDetailsOnly] = useState(false)
	const [pendingCancelRow, setPendingCancelRow] =
		useState<I_ApprovalLeaseRequest | null>(null)
	const [cancelling, setCancelling] = useState(false)

	const dispatch = useDispatch()

	const refresh = useCallback(
		() =>
			Promise.all([
				GetLeaseApprovalRequests(userId),
				shouldAllowAccess("FLEET:LEASEADMIN")
					? GetLeaseApprovalActions(userId)
					: Promise.resolve([] as I_ApprovalLeaseAction[]),
			]).then(([reqs, actions]) => {
				setRequests(reqs)
				setActionables(actions)
				dispatch(refreshActionableRequestCount())
			}),
		[userId, dispatch],
	)

	useEffect(() => {
		if (!userId || Number.isNaN(userId)) return
		setLoading(true)
		Promise.all([approvalService.init(), refresh()]).finally(() =>
			setLoading(false),
		)
	}, [refresh, userId])

	const activeRequests = requests.filter((r) => r.ActionedDateUTC === null)
	const completedRequests = requests.filter((r) => r.ActionedDateUTC !== null)

	const pendingActions = actionables
		.filter((r) => r.Status === "Pending")
		.sort(
			(a, b) =>
				new Date(a.CreatedDateUTC).getTime() -
				new Date(b.CreatedDateUTC).getTime(),
		)
	const completedActions = actionables
		.filter((r) => r.ActionedBy === userId)
		.sort((a, b) => {
			const aTime = a.ActionedDateUTC
				? new Date(a.ActionedDateUTC).getTime()
				: 0
			const bTime = b.ActionedDateUTC
				? new Date(b.ActionedDateUTC).getTime()
				: 0
			return bTime - aTime
		})

	const openRequest = (row: I_ApprovalLeaseRequest) => {
		setSelectedRequest(row)
	}

	const openAction = (row: I_ApprovalLeaseAction) => {
		setSelectedAction(row)
	}

	const closeDetails = () => {
		setSelectedRequest(null)
		setSelectedAction(null)
		setFetchLeaseDetailsOnly(false)
	}

	const actionOnRequest = async (actionName: "Approve" | "Reject") => {
		if (!selectedAction) return

		const actionTypeId = approvalService.reference.ApprovalActionTypes.find(
			(a) => a.Name === actionName,
		)?.ApprovalActionTypeId

		if (!actionTypeId) {
			console.error(
				`actionOnRequest: "${actionName}" not found in ApprovalActionTypes`,
				{ approvalServiceInitialized: approvalService.initialized },
			)
			snackBar({
				type: "error",
				text: "Approval configuration still loading. Please try again.",
				timeout: 4000,
			})
			return
		}

		const success = await ActionApproval({
			ApprovalStepId: selectedAction.ApprovalStepId,
			ActionedBy: userId,
			ApprovalActionTypeId: actionTypeId,
			Comments: null,
		})

		if (success) {
			closeDetails()
			refresh()
		}
	}

	const handleViewLeaseDetails = (
		action: I_ApprovalLeaseAction | null,
		request: I_ApprovalLeaseRequest | null,
	) => {
		setFetchLeaseDetailsOnly(true)
		setSelectedAction(action)
		setSelectedRequest(request)
	}

	const handleCancellationRequest = async (request: I_ApprovalLeaseRequest) => {
		const actionTypeId = approvalService.reference.ApprovalActionTypes.find(
			(a) => a.Name === "Reject",
		)?.ApprovalActionTypeId

		if (!actionTypeId) {
			console.error(
				`actionOnRequest: "Reject action type not found in ApprovalActionTypes`,
				{ approvalServiceInitialized: approvalService.initialized },
			)
			snackBar({
				type: "error",
				text: "Approval configuration still loading. Please try again.",
				timeout: 4000,
			})
			return
		}

		try {
			setCancelling(true)
			const success = await CancelLeaseRequest(request.ApprovalInstanceId)
			if (success) {
				snackBar({
					type: "success",
					text: "Request cancelled successfully!",
					timeout: 4000,
				})
				closeDetails()
				refresh()
			}
		} catch (_err) {
			snackBar({
				type: "error",
				text: "Error cancelling request",
				timeout: 4000,
			})
		} finally {
			setCancelling(false)
			setPendingCancelRow(null)
		}
	}

	if (loading) {
		return (
			<div className="RequestsManager">
				<Loader title="Loading requests" />
			</div>
		)
	}

	return (
		<div className="RequestsManager">
			{shouldAllowAccess("FLEET:LEASEADMIN") && (
				<div className="AdminPanel">
					<h2>Admin Panel</h2>
					<h4>
						Requires Action{" "}
						<small className="text-muted">(sorted by oldest first)</small>
					</h4>
					<RequestTable<I_ApprovalLeaseAction>
						rows={pendingActions}
						columns={pendingActionsColumns}
						onRowClick={openAction}
						rowKey={(r) => r.ApprovalStepId}
						emptyMessage="No requests currently need your attention."
						actions={(row) => (
							<Button
								onClick={() => handleViewLeaseDetails(null, row)}
								size="sm"
								variant="outline-secondary"
							>
								View Lease
							</Button>
						)}
					/>

					<h4>
						My Completed Actions{" "}
						<small className="text-muted">(sorted by newest first)</small>
					</h4>
					<RequestTable<I_ApprovalLeaseAction>
						rows={completedActions}
						columns={completedActionsColumns}
						onRowClick={openAction}
						rowKey={(r) => r.ApprovalStepId}
						emptyMessage="No completed actions."
					/>
				</div>
			)}

			<h4>
				My Active Requests{" "}
				<small className="text-muted">(sorted by newest first)</small>
			</h4>
			<RequestTable<I_ApprovalLeaseRequest>
				rows={activeRequests}
				columns={activeRequestsColumns}
				onRowClick={openRequest}
				rowKey={(r) => r.ApprovalInstanceId}
				emptyMessage="No current active requests."
				actions={(row) => (
					<div style={{ display: "flex", gap: "0.5rem" }}>
						<Button
							onClick={() => handleViewLeaseDetails(null, row)}
							size="sm"
							variant="outline-secondary"
						>
							View Lease
						</Button>
						<Button
							onClick={() => setPendingCancelRow(row)}
							size="sm"
							variant="outline-danger"
						>
							Cancel Request
						</Button>
					</div>
				)}
			/>

			<h4>
				My Completed Requests{" "}
				<small className="text-muted">(sorted by newest first)</small>
			</h4>
			<RequestTable<I_ApprovalLeaseRequest>
				rows={completedRequests}
				columns={completedRequestsColumns}
				onRowClick={openRequest}
				rowKey={(r) => r.ApprovalInstanceId}
				emptyMessage="No completed requests."
			/>
			<RequestDetailModal
				selectedRequest={selectedRequest}
				selectedAction={selectedAction}
				showCurrentLeaseOnly={fetchLeaseDetailsOnly}
				onClose={closeDetails}
				onAction={actionOnRequest}
			/>
			<Modal
				show={!!pendingCancelRow}
				onHide={() => !cancelling && setPendingCancelRow(null)}
				centered
				size="sm"
			>
				<Modal.Header closeButton={!cancelling}>
					<Modal.Title>Cancel Request</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					Are you sure you want to cancel the request for unit{" "}
					<strong>{pendingCancelRow?.UnitNumber}</strong>?
				</Modal.Body>
				<Modal.Footer>
					<Button
						variant="secondary"
						disabled={cancelling}
						onClick={() => setPendingCancelRow(null)}
					>
						No
					</Button>
					<Button
						variant="danger"
						disabled={cancelling}
						onClick={() =>
							pendingCancelRow && handleCancellationRequest(pendingCancelRow)
						}
					>
						{cancelling ? (
							<>
								<span
									className="spinner-border spinner-border-sm me-2"
									aria-hidden="true"
								/>
								Cancelling...
							</>
						) : (
							"Yes, cancel request"
						)}
					</Button>
				</Modal.Footer>
			</Modal>
		</div>
	)
}

export default RequestsManager
